import { Check, Copy, ThumbsDown, ThumbsUp } from "lucide-react";
import React from "react";
import { cn } from "src/common/utils";
import { Action, Actions } from "src/components/ai-elements/actions";
import {
  FeedbackType,
  useSubmitLlmChatFeedbackMutation,
} from "src/pages/AskPage/hooks/useSubmitLlmChatFeedbackMutation";

import { FeedbackModal } from "./FeedbackModal";
import { processMarkdownForClipboard } from "./processMarkdownForClipboard";

interface LlmChatMessageActionsProps {
  llmConversationId: string;
  llmMessageId: string;
  messageContent: string;
  className?: string;
}

const FEEDBACK_STORAGE_PREFIX = "chat-feedback";

const buildFeedbackKey = (conversationId: string, messageId: string) =>
  `${FEEDBACK_STORAGE_PREFIX}-${conversationId}-${messageId}`;

const readFeedback = (llmConversationId: string, llmMessageId: string): FeedbackType | null => {
  try {
    const stored = localStorage.getItem(buildFeedbackKey(llmConversationId, llmMessageId));
    return stored === "THUMBS_UP" || stored === "THUMBS_DOWN" ? stored : null;
  } catch {
    return null;
  }
};

const writeFeedback = (llmConversationId: string, llmMessageId: string, feedback: FeedbackType) => {
  try {
    localStorage.setItem(buildFeedbackKey(llmConversationId, llmMessageId), feedback);
  } catch {
    // Ignore storage failures (e.g., Safari private mode)
  }
};

const clearFeedback = (llmConversationId: string, llmMessageId: string) => {
  try {
    localStorage.removeItem(buildFeedbackKey(llmConversationId, llmMessageId));
  } catch {
    // Ignore storage failures
  }
};

const useLlmChatMessageFeedback = (llmConversationId: string, llmMessageId: string) => {
  const { handleSubmitFeedback, isSubmittingFeedback } = useSubmitLlmChatFeedbackMutation();
  const [currentFeedback, setCurrentFeedback] = React.useState<FeedbackType | null>(null);

  React.useEffect(() => {
    setCurrentFeedback(readFeedback(llmConversationId, llmMessageId));
  }, [llmConversationId, llmMessageId]);

  const handleFeedbackClick = React.useCallback(
    (feedbackType: FeedbackType, feedbackText?: string, feedbackTags?: string[]) => {
      setCurrentFeedback((previousFeedback) => {
        const isRemoving = previousFeedback === feedbackType;

        if (isRemoving) {
          clearFeedback(llmConversationId, llmMessageId);
          return null;
        }

        writeFeedback(llmConversationId, llmMessageId, feedbackType);
        handleSubmitFeedback(llmMessageId, llmConversationId, feedbackType, feedbackText, feedbackTags);
        return feedbackType;
      });
    },
    [llmConversationId, handleSubmitFeedback, llmMessageId],
  );

  return {
    currentFeedback,
    handleFeedbackClick,
    isSubmittingFeedback,
  };
};

export const LlmChatMessageActions: React.FC<LlmChatMessageActionsProps> = ({
  llmConversationId,
  llmMessageId,
  messageContent,
  className,
}) => {
  const [isCopied, setIsCopied] = React.useState(false);
  const [isFeedbackModalOpen, setIsFeedbackModalOpen] = React.useState(false);
  const copyTimeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const { currentFeedback, handleFeedbackClick, isSubmittingFeedback } = useLlmChatMessageFeedback(
    llmConversationId,
    llmMessageId,
  );

  React.useEffect(() => {
    return () => {
      if (copyTimeoutRef.current) {
        clearTimeout(copyTimeoutRef.current);
        copyTimeoutRef.current = null;
      }
    };
  }, []);

  const handleCopy = React.useCallback(async () => {
    if (copyTimeoutRef.current) {
      clearTimeout(copyTimeoutRef.current);
      copyTimeoutRef.current = null;
    }
    try {
      const processedContent = await processMarkdownForClipboard(messageContent);
      await navigator.clipboard.writeText(processedContent);
      setIsCopied(true);
    } catch (error) {
      console.error("Failed to copy processed content", error);
      try {
        await navigator.clipboard.writeText(messageContent);
        setIsCopied(true);
      } catch (fallbackError) {
        console.error("Failed to copy fallback content", fallbackError);
      }
    }

    copyTimeoutRef.current = setTimeout(() => {
      setIsCopied(false);
      copyTimeoutRef.current = null;
    }, 2500);
  }, [messageContent]);

  const handleThumbsDownClick = React.useCallback(() => {
    // If already thumbs-down, remove it
    if (currentFeedback === "THUMBS_DOWN") {
      handleFeedbackClick("THUMBS_DOWN");
    } else {
      // Open modal to collect feedback
      setIsFeedbackModalOpen(true);
    }
  }, [currentFeedback, handleFeedbackClick]);

  const handleFeedbackSubmit = React.useCallback(
    (tags: string[], text: string) => {
      // Submit thumbs-down with feedback details
      handleFeedbackClick("THUMBS_DOWN", text, tags);
      setIsFeedbackModalOpen(false);
    },
    [handleFeedbackClick]
  );

  return (
    <Actions className={cn("text-muted-foreground", className)}>
      <Action
        onClick={handleCopy}
        tooltip={isCopied ? "Copied" : "Copy"}
        aria-pressed={isCopied}
        className={cn(
          "transition-colors",
          isCopied
            ? "text-emerald-400 hover:bg-emerald-950/50 hover:text-emerald-300"
            : "hover:text-foreground",
        )}
        label="Copy response"
        size="icon"
      >
        {isCopied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
      </Action>
      <Action
        onClick={() => handleFeedbackClick("THUMBS_UP")}
        disabled={isSubmittingFeedback}
        tooltip="Good response"
        aria-pressed={currentFeedback === "THUMBS_UP"}
        className={cn(
          "transition-colors",
          currentFeedback === "THUMBS_UP"
            ? "text-emerald-400 hover:bg-emerald-950/50 hover:text-emerald-300"
            : "hover:text-foreground",
        )}
        label="Thumbs up"
        size="icon"
      >
        <ThumbsUp className="h-4 w-4" />
      </Action>
      <Action
        onClick={handleThumbsDownClick}
        disabled={isSubmittingFeedback}
        tooltip="Bad response"
        aria-pressed={currentFeedback === "THUMBS_DOWN"}
        className={cn(
          "transition-colors",
          currentFeedback === "THUMBS_DOWN"
            ? "text-red-400 hover:bg-red-950/50 hover:text-red-300"
            : "hover:text-foreground",
        )}
        label="Thumbs down"
        size="icon"
      >
        <ThumbsDown className="h-4 w-4" />
      </Action>

      <FeedbackModal
        open={isFeedbackModalOpen}
        onOpenChange={setIsFeedbackModalOpen}
        onSubmit={handleFeedbackSubmit}
        isSubmitting={isSubmittingFeedback}
      />
    </Actions>
  );
};
